import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Grid, Stack } from '@mui/material';
import { AccountStatusCard } from './AccountStatusCard';
import type { IProfileFormValues } from './ServiceProviderProfileForm';
import { ServiceProviderProfileInfo } from './ServiceProviderProfileForm';
import {
  useGetServiceProviderPhoenix,
  useUpdateServiceProviderPhoenix,
  useUserProfileApi,
} from '@pflegenavi/service-provider-frontend/api';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import type { UserProfileDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderStripe } from './ServiceProviderStripe';
import { Kyc } from './Kyc';
import { BankAccountCard } from './BankAccountCard';

export const ServiceProviderProfileDetails: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data: phoenixResponse } = useGetServiceProviderPhoenix();

  const { mutateAsync: updateServiceProvider } =
    useUpdateServiceProviderPhoenix();

  const userProfileApi = useUserProfileApi();
  const [userProfile, setUserProfile] = useState<UserProfileDto | undefined>(
    undefined
  );

  useEffect(() => {
    (async () => {
      const userProfile = await userProfileApi.getUserProfile();
      setUserProfile(userProfile);
    })();
  }, [userProfileApi]);

  const handleUpdateServiceProvider = useCallback(
    async (values: IProfileFormValues) => {
      try {
        await updateServiceProvider({
          name: values.companyName,
          address: values.address,
          zip_code: values.zipCode,
          city: values.city,
          phone: values.phone,
          country_id: values.countryId,
        });
        enqueueSnackbar(
          t('service-provider-app.profile.successfully-updated'),
          {
            variant: 'success',
          }
        );
      } catch {
        enqueueSnackbar(t('service-provider-app.profile.failed-to-update'), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, t, updateServiceProvider]
  );

  if (!userProfile || !phoenixResponse) {
    return (
      <Stack alignItems="center">
        <CircularProgress
          color="primary"
          variant="indeterminate"
          sx={{ mt: 8 }}
        />
      </Stack>
    );
  }

  const { data } = phoenixResponse;
  const paymentProcessor = data.payment_service_provider ?? 'mangopay';
  const isOwnedByPflegenavi = data.stripe_account?.owned_by_pflegenavi ?? false;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={paymentProcessor === 'mangopay' ? 6 : 8}>
        <ServiceProviderProfileInfo
          userProfile={userProfile}
          company={{
            id: data.global_service_provider_id,
            name: data.name,
            address: data.address ?? '',
            zipCode: data.zip_code ?? '',
            city: data.city ?? '',
            phone: data.phone ?? '',
            countryId: data.country_id ?? undefined,
          }}
          handleUpdateServiceProvider={handleUpdateServiceProvider}
        />
      </Grid>
      <Grid item xs={12} lg={paymentProcessor === 'mangopay' ? 6 : 4}>
        <Stack direction="column" gap={3}>
          <AccountStatusCard
            active={true}
            activeSince={userProfile.user.createdAt}
          />
          {!isOwnedByPflegenavi && paymentProcessor === 'stripe' && (
            <ServiceProviderStripe />
          )}
          {paymentProcessor === 'mangopay' && (
            <>
              <BankAccountCard />
              <Kyc />
            </>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};
